import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Locations', path: '/locations' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
          scrolled
            ? 'border-b border-gray-100 shadow-sm'
            : 'border-b border-gray-100'
        }`}
      >
        <div className="px-6 lg:px-14 h-[80px] flex items-center justify-between gap-8">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 cursor-pointer">
            <img
              src="https://storage.readdy-site.link/project_files/2083ce7f-e0ee-4a4d-a433-155c56b9a2a6/d757ef0e-ddc9-4ec4-93c1-b4ecfa73f897_logo-5.png?v=746ece2dbf6fac043e1648af4619e15a"
              alt="Atlantis HVAC Systems"
              className="h-[52px] w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-caps text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 whitespace-nowrap cursor-pointer relative group ${
                  location.pathname === link.path
                    ? 'text-luxury-gold'
                    : 'text-luxury-text-mid hover:text-luxury-gold'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-luxury-gold transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:9058454009"
              className="hidden lg:flex items-center gap-2.5 cursor-pointer whitespace-nowrap group"
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-full border border-luxury-gold/25 group-hover:border-luxury-gold/60 transition-colors">
                <i className="ri-phone-line text-luxury-gold text-xs" />
              </div>
              <div className="flex flex-col">
                <span className="font-caps text-[9px] tracking-[0.2em] uppercase text-luxury-text-muted leading-none">GTA West</span>
                <span className="font-caps text-[11px] tracking-wider text-luxury-text-mid group-hover:text-luxury-gold transition-colors leading-none mt-0.5">
                  (905) 845-4009
                </span>
              </div>
            </a>
            <a
              href="tel:9058454009"
              className="flex items-center gap-2.5 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-bold text-[11px] tracking-[0.18em] uppercase px-6 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-check-line text-xs" />
              </div>
              <span>Free Quote</span>
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-11 h-11 flex flex-col items-center justify-center gap-[5px] cursor-pointer flex-shrink-0"
            aria-label="Toggle navigation menu"
          >
            <span
              className={`block w-6 h-[1.5px] bg-luxury-text-dark transition-all duration-300 ${
                mobileOpen ? 'rotate-45 translate-y-[6.5px]' : ''
              }`}
            />
            <span
              className={`block w-6 h-[1.5px] bg-luxury-text-dark transition-all duration-300 ${
                mobileOpen ? 'opacity-0 scale-x-0' : ''
              }`}
            />
            <span
              className={`block w-6 h-[1.5px] bg-luxury-text-dark transition-all duration-300 ${
                mobileOpen ? '-rotate-45 -translate-y-[6.5px]' : ''
              }`}
            />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-500 overflow-hidden ${
            mobileOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
          } bg-white border-t border-gray-100`}
        >
          <nav className="flex flex-col px-7 py-8 gap-6">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-caps text-sm tracking-[0.18em] uppercase transition-colors cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-luxury-gold'
                    : 'text-luxury-text-mid hover:text-luxury-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-gray-100 pt-6 flex flex-col gap-3">
              <a href="tel:9058454009" className="text-luxury-gold font-caps text-sm tracking-widest cursor-pointer whitespace-nowrap">
                GTA West: (905) 845-4009
              </a>
              <a href="tel:4168913444" className="text-luxury-gold font-caps text-sm tracking-widest cursor-pointer whitespace-nowrap">
                GTA East: (416) 891-3444
              </a>
              <a
                href="tel:9058454009"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-luxury-gold text-white font-caps font-bold text-xs tracking-[0.15em] uppercase py-3.5 rounded-full cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-xs" />
                <span>Book Free Quote</span>
              </a>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
}
